// DEV-ONLY local operator login — sign in WITHOUT Keycloak and without pasting
// a token into the console. The customer-web twin (apps/customer-web/src/auth/
// dev-auth.ts) is the shape this follows; the differences are all operator
// facts, not style:
//   * the mint is GET /ops/v1/dev/login (spec 60 — operator and customer
//     consoles are separate products, so this is NOT a `realm` param on the
//     customer route),
//   * its index carries no tenant_id (operator tokens are cross-tenant),
//   * the roster is roles-shaped: which of the 8 role_op_* a persona holds is
//     the whole point, since every ops route carries its own frozen role list.
//
// CARDINAL SAFETY RULE: DEV-ONLY. Everything is gated behind
// isOperatorDevAuthActive() (→ import.meta.env.DEV), so a production build
// never reaches this code and Vite tree-shakes the module out. The BACKEND
// gate is stricter and independent (NODE_ENV + AUTH_DEV_HMAC_SECRET +
// OPERATOR_DEV_LOGIN_ENABLED=on); this client flag cannot widen it.
//
// MFA: tokens minted here carry NO auth_time/amr, so requireFreshMfa fails
// closed and every MFA-gated operator MUTATION stays unreachable. Read-only
// operator surfaces work on realm + roles alone. That is a property of the
// mint, not a limitation to work around here.
//
// How it works (no oidc storage-key hardcoding — version-proof): a UserManager
// built from the SAME settings oidc-config feeds react-oidc-context calls
// storeUser(), so the user lands on the EXACT key that provider reads and
// AuthProvider hydrates it on mount.

import { User, UserManager, type UserManagerSettings, type UserProfile } from "oidc-client-ts";

import { authority, clientId, redirectUri, userStore } from "@/auth/oidc-config";
import { isOperatorDevAuthActive } from "@/auth/dev-auth-flag";
import { OPS_DEV_USER_STORAGE_KEY } from "@/auth/dev-auth-storage";
import { operatorGraphBaseUrl } from "@/api/operator-graph";

export { isOperatorDevAuthActive };
export { OPS_DEV_USER_STORAGE_KEY } from "@/auth/dev-auth-storage";

// ---------------------------------------------------------------------------
// Contract — GET /ops/v1/dev/login. Typed locally: the dev endpoint is not in
// the generated OpenAPI client the app ships against.
// ---------------------------------------------------------------------------

/** The OIDC profile claims the backend returns for a chosen dev operator. */
export interface OpsDevLoginProfile {
  readonly sub: string;
  readonly preferred_username: string;
  readonly name: string;
  readonly email: string;
  readonly locale: string;
  readonly iss: string;
  readonly roles: readonly string[];
}

/** One row of the roster listing — drives the switcher. */
export interface OpsDevUserSummary {
  readonly key: string;
  readonly name: string;
  readonly role: string;
  readonly email: string;
}

/** GET /ops/v1/dev/login?user=<key> success payload. */
export interface OpsDevLoginSession {
  readonly token: string;
  /** unix seconds — equals the JWT exp. */
  readonly expires_at: number;
  readonly profile: OpsDevLoginProfile;
}

/**
 * Fallback persona when none is chosen. `opadmin` deliberately: it is the
 * BROADEST persona, so a first-run developer sees the most of the console —
 * and it is still not universal (Licenses is revenue-only by separation of
 * duties), which is exactly the property the rail's role gating encodes.
 */
export const DEFAULT_OPS_DEV_USER_KEY = "opadmin";

/** The dev-session TTL the backend mints (8h) — used only to approximate iat. */
const OPS_DEV_TTL_SECONDS = 8 * 60 * 60;

/**
 * The storage key react-oidc-context's UserManager reads its user from. We
 * WRITE via storeUser() (which derives this internally); this helper exists so
 * a test can assert the key the library lands on, and so callers can clear it.
 */
export function opsDevUserStorageKey(auth: string, client: string): string {
  return `oidc.user:${auth}:${client}`;
}

/**
 * Build an oidc-client-ts `User` from the backend session, so the app's
 * existing Bearer injector picks the token up exactly like a real Keycloak
 * login. The access token is the authority on the wire — the server
 * re-verifies its HS256 signature; these profile fields only populate the
 * SPA's own user object.
 */
export function buildOpsDevUser(session: OpsDevLoginSession, client: string): User {
  const { token, expires_at, profile } = session;
  const userProfile: UserProfile = {
    ...profile,
    aud: client,
    exp: expires_at,
    iat: expires_at - OPS_DEV_TTL_SECONDS,
  };
  return new User({
    access_token: token,
    id_token: token,
    token_type: "Bearer",
    scope: "openid profile email",
    profile: userProfile,
    expires_at,
  });
}

/**
 * True iff `user` is the live session for `key` — unexpired AND minted for
 * that persona.
 *
 * The identity half matters as much as expiry: an expiry-only check makes
 * bootstrapOperatorDevAuth() read "some valid user is stored" as "the selected
 * operator is signed in", so writing novemai.opsDevAuthUser directly leaves
 * the previous operator's token on every request while the switcher names the
 * new one. On this console that silently widens role coverage — role_op_admin
 * would keep answering while the screen claims to be role_op_auditor.
 *
 * The discriminator is `preferred_username` (ops-dev-login.ts sets it to the
 * roster key); `sub` here is the entity_internal_user UUID, not derivable
 * from the key.
 */
export function storedUserMatchesKey(user: User, key: string): boolean {
  return user.expired === false && user.profile.preferred_username === key;
}

function hasValidStoredUserFor(key: string): boolean {
  if (userStore === undefined) return false;
  const raw = window.sessionStorage.getItem(opsDevUserStorageKey(authority, clientId));
  if (raw === null) return false;
  try {
    return storedUserMatchesKey(User.fromStorageString(raw), key);
  } catch {
    return false;
  }
}

/** Read the chosen dev operator key (defaults to the broadest persona). */
export function getSelectedOpsDevUserKey(): string {
  if (typeof window === "undefined") return DEFAULT_OPS_DEV_USER_KEY;
  return window.localStorage.getItem(OPS_DEV_USER_STORAGE_KEY) ?? DEFAULT_OPS_DEV_USER_KEY;
}

/** Persist the chosen dev operator key for subsequent reloads. */
export function setSelectedOpsDevUserKey(key: string): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(OPS_DEV_USER_STORAGE_KEY, key);
}

function clearStoredOidcUser(): void {
  if (userStore === undefined) return;
  window.sessionStorage.removeItem(opsDevUserStorageKey(authority, clientId));
}

/** A UserManager whose settings match react-oidc-context's exactly. */
function devUserManager(): UserManager {
  const settings: UserManagerSettings = {
    authority,
    client_id: clientId,
    redirect_uri: redirectUri,
    // Only set when defined (exactOptionalPropertyTypes); callers gate on
    // userStore !== undefined before reaching here.
    ...(userStore !== undefined ? { userStore } : {}),
  };
  return new UserManager(settings);
}

/**
 * Fetch the seeded dev-operator roster (for the switcher). Returns [] on any
 * error — including the 404 a server without OPERATOR_DEV_LOGIN_ENABLED=on
 * returns, which is the expected answer, not a fault.
 */
export async function fetchOpsDevUsers(): Promise<readonly OpsDevUserSummary[]> {
  try {
    const res = await fetch(`${operatorGraphBaseUrl}/ops/v1/dev/login`, {
      credentials: "include",
    });
    if (!res.ok) return [];
    const body: unknown = await res.json();
    return (body as { data?: { users?: readonly OpsDevUserSummary[] } }).data?.users ?? [];
  } catch {
    return [];
  }
}

async function fetchOpsDevSession(key: string): Promise<OpsDevLoginSession | null> {
  try {
    const res = await fetch(
      `${operatorGraphBaseUrl}/ops/v1/dev/login?user=${encodeURIComponent(key)}`,
      { credentials: "include" },
    );
    if (!res.ok) return null;
    const body: unknown = await res.json();
    return (body as { data?: OpsDevLoginSession }).data ?? null;
  } catch {
    return null;
  }
}

/**
 * DEV-ONLY: ensure a valid OIDC user exists in sessionStorage BEFORE
 * AuthProvider mounts, so RequireOperatorAuth never kicks off a Keycloak
 * redirect against an authority that isn't running.
 *
 * No-op in a production build, and when the stored user IS the selected
 * operator. Any backend error is swallowed: the app falls through to the
 * normal signed-out path rather than crashing on boot.
 */
export async function bootstrapOperatorDevAuth(): Promise<void> {
  if (!isOperatorDevAuthActive()) return;
  if (userStore === undefined) return;

  const key = getSelectedOpsDevUserKey();
  if (hasValidStoredUserFor(key)) return;

  const session = await fetchOpsDevSession(key);
  if (session === null) return;

  await devUserManager().storeUser(buildOpsDevUser(session, clientId));
}

/**
 * Switcher action: select a different dev operator. Persists the choice,
 * clears the current user, and reloads so the bootstrap re-runs as the newly
 * chosen persona.
 */
export function selectOpsDevUser(key: string): void {
  setSelectedOpsDevUserKey(key);
  clearStoredOidcUser();
  window.location.assign("/");
}
